import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Modal } from '@els/els-react--modal';
import { Icon } from '@els/els-react--icon';
import { dispatchCreateAlert } from '@Pages/Alerts/AlertsComponents/AlertCreateDialog/AlertCreateDialogActions';
import { ButtonWithIcon, Button } from '@els/els-react--button';
import { LinkElement } from '@els/els-react--link-element';
import InnerHTML from '@Components/InnerHTML';
import { LEGACY_DIALOG_SIZE } from '@Containers/Dialogs/DialogsConstants';

import './DialogWithErrorLayout.scss';

export default function DialogWithErrorLayout(props) {
  const {
    selfClose,
    onYesClick,
    className,
    headerClassName,
    headerTitleClassName,
    contentClassName,
    footerClassName,
    header,
    children,
    content,
    yesLabel = 'Got it',
    traceInfo,
    link,
    crumb,
  } = props;

  const clickHandler = () => {
    selfClose();
    if (onYesClick) {
      onYesClick();
    }
  };

  const createAlert = () => {
    selfClose();
    dispatchCreateAlert(crumb);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(traceInfo?.traceparent?.toUpperCase());
  };

  const getTraceErrorPreHeaderContent = () => {
    if (traceInfo && (traceInfo.traceparent || traceInfo.timestamp)) {
      return (
        <div className="dialog-with-error-layout-pre_header">
          {traceInfo.timestamp && (
            <InnerHTML tag="div" html={traceInfo.timestamp instanceof Date ? traceInfo.timestamp.toISOString() : traceInfo.timestamp} />
          )}
          {traceInfo.traceparent && (
            <div className="dialog-with-error-layout-pre_header--trace-label">
              Reference:&nbsp;
              <Button
                data-e2e="copy-trace-info-button"
                type="link"
                size="small"
                title="Copy to Clipboard"
                onClick={copyToClipboard}
                className="dialog-with-error-layout-pre_header--trace-value e2e-traceparent">
                {traceInfo.traceparent.toUpperCase()}
              </Button>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const getLinkContent = () => {
    if (link) {
      return (
        <span className="dialog-with-error-layout-content-link">
          {link.preMessage}
          <LinkElement data-e2e="dialog-error-link" href={link.url} isExternal>
            {link.linkText}
          </LinkElement>
          {link.postMessage}
        </span>
      );
    }
    return null;
  };

  return (
    <Modal type="negative" onClose={selfClose} className={classnames(className, 'dialog-with-error-layout')} width={LEGACY_DIALOG_SIZE}>
      {header && (
        <header className={classnames(headerClassName, 'dialog-with-error-layout-header', 'u-els-margin-bottom-1o2')}>
          {getTraceErrorPreHeaderContent()}
          <div className="o-els-flex-layout o-els-flex-layout--left o-els-flex-layout--middle u-els-margin-top-1o2 u-els-margin-bottom-1o2">
            <Icon sprite="AlertSolidSquare" size="m" color="negative">
              Error
            </Icon>
            <h4 className={classnames(headerTitleClassName, 'dialog-with-error-layout-header-title', 'u-els-margin-left')}>{header}</h4>
          </div>
        </header>
      )}
      <section
        className={classnames(contentClassName, 'dialog-with-error-layout-content-wrapper', 'u-els-margin-left-3x u-els-margin-bottom')}>
        {children || content}
        {getLinkContent()}
      </section>
      <footer
        className={classnames(footerClassName, 'dialog-with-error-layout-footer', 'u-els-margin-right-1o2', { 'alert-footer': crumb })}>
        {crumb && (
          <ButtonWithIcon
            data-e2e="create-alert-button"
            type="link"
            size="small"
            linkIconSize="s"
            sprite="Alarm"
            iconRight
            onClick={createAlert}
            className="e2e-breadcrumb-alert">
            Alert
          </ButtonWithIcon>
        )}
        <ButtonWithIcon
          data-e2e="dialog-error-continue-button"
          type="primary"
          size="small"
          sprite="ChevronRight"
          linkIconSize="s"
          iconRight
          onClick={clickHandler}
          className="e2e-dialog-error-ok">
          {yesLabel}
        </ButtonWithIcon>
      </footer>
    </Modal>
  );
}

DialogWithErrorLayout.propTypes = {
  selfClose: PropTypes.func.isRequired,
  onYesClick: PropTypes.func,
  yesLabel: PropTypes.string,
  className: PropTypes.string,
  headerClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  footerClassName: PropTypes.string,
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  headerTitleClassName: PropTypes.string,
  children: PropTypes.node,
  content: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  traceInfo: PropTypes.object,
  link: PropTypes.object,
  crumb: PropTypes.object,
};
